import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  Scale,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * TIER-3 q4 (law firm). Targets "organize law firm files by matter" and the
 * matter-centric filing problem: a paralegal or solo attorney with a pile of
 * pleadings, correspondence, discovery, contracts, and billing PDFs that all
 * need to land under the right Client > Matter > category folder with a
 * consistent date + matter + doctype filename. Sortio reads each document,
 * identifies the matter, and files it; preview before apply; local inference
 * for confidentiality; one shared rule the whole firm runs. Links to
 * /for-law-firms and the firm-workflow siblings (naming convention, audit
 * readiness, per-client batching).
 */

const PUBLISHED = '2026-06-05';
const UPDATED = '2026-06-05';
const CANONICAL = 'https://www.getsortio.com/blog/organize-law-firm-files-by-matter';

const FAQ = [
  {
    question: 'How do you organize client files by matter at a law firm?',
    answer:
      'You file by a three-level structure: Client, then Matter, then document category (Pleadings, Correspondence, Discovery, Contracts, Billing). Every document gets named with the date, the matter, and the document type so it sorts chronologically and is identifiable on sight. Sortio reads each file, identifies which client and matter it belongs to from the content, and routes it into the right category folder with a consistent name, so a mixed pile of downloads becomes a clean matter file in one pass.',
  },
  {
    question: 'What folder structure should a law firm use for matters?',
    answer:
      "The durable structure is Clients > {Client} > {MatterNumber}_{MatterName} > {Category}, where category is one of Pleadings, Correspondence, Discovery, Contracts, and Billing. Keying the matter folder with a matter number first means it sorts and stays stable even if the matter name changes. Inside each category, name files {YYYY-MM-DD}_{MatterNumber}_{DocType}_{Description}.pdf so everything in a folder sorts by date and reads cleanly. This mirrors how most firms already think about a file, it just enforces it consistently across everyone's downloads.",
  },
  {
    question: 'Can software file a document under the right matter automatically?',
    answer:
      'Yes. Sortio reads the content of each document, finds the matter identifier (a matter number, a caption, a client name, a case caption block), and routes the file under that matter. It does not rely on the filename, which is usually useless on a downloaded pleading or a scanned signature page. You preview every proposed destination and name before anything moves, so the attorney or paralegal stays in control of where each document lands.',
  },
  {
    question: 'Is it safe to use AI on confidential legal documents?',
    answer:
      'Sortio supports local-only inference through Ollama, so the model runs on the machine and no document content leaves it. For privileged material, discovery, or anything under a protective order, that is the configuration to use. The managed AI option is faster and more accurate and is appropriate for routine, non-privileged filing. Either way Sortio is non-destructive, previews before applying, and keeps 30-day backups of anything it renames or moves so a mistaken file is always recoverable.',
  },
  {
    question: 'How do you keep the whole firm filing the same way?',
    answer:
      'Write the matter-filing rule once and share it. On Sortio Teams ($29 per seat per month) the same prompt and the same folder conventions are shared across the firm with an admin console, so every paralegal and associate files identically instead of inventing their own scheme. New hires inherit the convention on day one rather than learning one attorney\'s personal habits.',
  },
  {
    question: 'Does this replace our document management system like NetDocuments or iManage?',
    answer:
      'No. Sortio is the layer before your DMS. It takes raw local files (email attachments, downloads, scans, signature pages) and gets them named and filed correctly under the right matter so that what goes into NetDocuments, iManage, or whatever system you run is already clean and consistently labeled. Sortio handles the messy local-disk stage; your DMS handles long-term storage, versioning, and access control.',
  },
  {
    question: 'How is reading content different from a Finder rule or a Hazel rule?',
    answer:
      'A Finder or Hazel rule matches a fixed pattern against the filename, the extension, or the document text. That works for predictable, single-source files and breaks the moment a document arrives with a useless name or a layout the pattern was never written for. Sortio acts on the meaning of the document, so it can tell a motion from a deposition transcript from an invoice and file each correctly even when the filenames are identical garbage. macOS Finder has no built-in auto rename or route engine at all, so the realistic alternatives are Hazel rules or manual filing, both of which struggle with content-level routing.',
  },
];

const OrganizeLawFirmFilesByMatterBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Organize client files by matter at a law firm', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Organize client files by matter at a law firm',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Organize Client Files by Matter at a Law Firm',
    description:
      'A matter-centric filing system for law firms: Client > Matter > category (Pleadings, Correspondence, Discovery, Contracts, Billing), a date plus matter plus doctype naming pattern, and how Sortio reads each document, identifies the matter, and files it with preview and local inference.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: UPDATED,
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="How to Organize Client Files by Matter at a Law Firm"
        description="Build a matter-centric filing system: Client > Matter > Pleadings/Correspondence/Discovery/Contracts/Billing, with a date plus matter plus doctype naming pattern. Sortio reads each document, identifies the matter, and files it. Preview, local inference, one shared firm rule."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="organize law firm files by matter, law firm file organization, matter centric filing, legal document naming convention, organize legal files by client, file pleadings discovery correspondence, law firm folder structure, paralegal document filing"
        lastModified={UPDATED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            How to Organize Client Files by Matter at a Law Firm
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>Updated {new Date(UPDATED).toLocaleDateString()}</span>
            <span>10 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            The Smith matter has documents in three places: the named partner&apos;s Downloads
            folder, a shared drive folder called "Smith stuff," and an email thread nobody has
            exported. A pleading is named document(4).pdf. A signed engagement letter is a scan
            named img_2231.pdf. When opposing counsel asks for a specific exhibit, someone spends
            twenty minutes hunting. Every firm that has not enforced a filing system lives some
            version of this, and it gets worse with every new matter.
          </p>

          <p>
            The fix is not another reminder to "name your files properly." People will not do it
            by hand, and the documents that matter most (downloaded pleadings, scanned signature
            pages, attachments) arrive with the worst names. The fix is a matter-centric structure
            plus a tool that reads each document, figures out which matter it belongs to, and files
            it there. This post is the full walkthrough: the folder structure, the naming pattern,
            and how Sortio handles the routing with one shared rule the whole firm can run.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <Scale className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  Organize by Client {'>'} Matter {'>'} category (Pleadings, Correspondence,
                  Discovery, Contracts, Billing), and name every file{' '}
                  {'{YYYY-MM-DD}_{MatterNumber}_{DocType}_{Description}.pdf'}. Doing this by hand
                  fails because downloaded and scanned documents have useless names. Sortio reads
                  each document, identifies the matter from the content, and files it into the
                  right category folder with a consistent name. Preview before anything moves, run
                  inference locally for confidential material, and share one rule across the firm.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How to organize client files by matter at a law firm
          </h2>

          <p>
            To organize client files by matter at a law firm, file every document under a
            three-level structure (Client, then Matter, then document category) and name each file
            with the date, the matter, and the document type. Sortio makes this practical by
            reading the content of each document, identifying which client and matter it belongs
            to, and routing it into the correct category folder automatically, so a mixed pile of
            downloads and scans becomes a clean, navigable matter file instead of a manual sorting
            chore nobody finishes.
          </p>

          <p>
            The reason matter-centric beats every other scheme is that a law firm&apos;s unit of
            work is the matter, not the document type and not the date. An attorney thinks "pull
            the Smith dissolution file," not "pull all the PDFs from April." So the structure has
            to make the matter the top-level lookup, with categories underneath so you can go
            straight to the pleadings or the discovery without scrolling past everything else.
            It also matters long after the file closes: ABA Model Rule 1.15 requires that complete
            records of client funds and property be{' '}
            <a
              href="https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/rule_1_15_safekeeping_property/"
              className="text-sortio-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              preserved for five years after the representation ends
            </a>
            , and a matter-keyed archive is what makes a request that old answerable without a
            manual hunt through three people&apos;s Downloads folders.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The matter-centric folder structure
          </h2>

          <p>
            Here is the structure that holds up across a growing caseload. The top level is the
            client, the second level is the matter (keyed with a matter number so it stays stable),
            and the third level is the document category:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Clients/
  Smith_John/
    2026-0142_Dissolution_of_Marriage/
      Pleadings/
      Correspondence/
      Discovery/
      Contracts/
      Billing/
    2025-0098_Estate_Planning/
      Pleadings/
      Correspondence/
      Discovery/
      Contracts/
      Billing/
  Acme_Corp/
    2026-0207_Commercial_Lease_Dispute/
      Pleadings/
      Correspondence/
      Discovery/
      Contracts/
      Billing/`}
          </pre>

          <p>
            Five categories cover almost everything a litigation or transactional matter
            accumulates. Pleadings holds filings (complaints, answers, motions, orders).
            Correspondence holds letters and emails to and from clients, opposing counsel, and the
            court. Discovery holds requests, responses, document productions, and deposition
            materials. Contracts holds agreements, engagement letters, and signed instruments.
            Billing holds invoices, time exports, and trust accounting. Matters that need a sixth
            bucket (for example Exhibits or Research) can add one, but five is the spine.
          </p>

          <p>
            The matter number prefix (2026-0142) does the heavy lifting. It sorts matters
            chronologically by open date, it survives a matter being renamed, and it gives every
            document a short stable token to carry in its filename. If your firm already issues
            matter numbers from its billing system, reuse those. If not, year plus a sequential
            counter is fine.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The naming pattern: date, matter, document type
          </h2>

          <p>
            Inside each category folder, every file follows one pattern so the folder sorts by date
            and reads cleanly on sight:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Pattern:
{YYYY-MM-DD}_{MatterNumber}_{DocType}_{Description}.pdf

Examples:
2026-03-04_2026-0142_Motion_Temporary_Orders.pdf
2026-03-11_2026-0142_Letter_to_Opposing_Counsel.pdf
2026-03-18_2026-0142_Discovery_Responses_Set_One.pdf
2026-02-20_2026-0142_Engagement_Letter_Signed.pdf
2026-04-01_2026-0142_Invoice_March.pdf`}
          </pre>

          <p>
            Date first (ISO YYYY-MM-DD) means a category folder always reads top to bottom in
            chronological order without a custom Finder view. The matter number next ties the file
            to its matter even if it is ever copied somewhere else or attached to an email. The
            document type (Motion, Letter, Discovery, Contract, Invoice) makes the folder skimmable.
            The description disambiguates documents of the same type on the same day. Extension as
            expected.
          </p>

          <p>
            This is the kind of convention that is easy to define and almost impossible to enforce
            by hand, which is exactly why most firms have one in a wiki that nobody follows. The
            way to make it real is to generate the name from the document content automatically. If
            you want a reusable, firm-wide spec for this, see{' '}
            <Link to="/blog/enforce-file-naming-convention" className="text-sortio-primary hover:underline">
              how to enforce a file naming convention
            </Link>{' '}
            and the{' '}
            <Link to="/tools/file-naming-convention-generator" className="text-sortio-primary hover:underline">
              file naming convention generator
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Manual vs rule-based vs read-the-content
          </h2>

          <p>
            There are three honest ways to get documents into the right matter folder, and they
            suit different volumes and different document quality.
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Manual filing
  How:    A person drags each file to the right
          Client > Matter > Category folder and renames it.
  Good:   Total control. No tooling.
  Bad:    Does not scale. Skipped under deadline pressure.
          New hires file inconsistently.

Rule-based (Hazel / AI Rule Builder)
  How:    Deterministic rules on filename, extension, or a
          regex against the text layer. No AI allowance used.
  Good:   Fast, free per run, predictable. Great when a source
          always produces the same predictable file.
  Bad:    Deterministic patterns are brittle. A scan or a
          new layout that does not match the pattern is
          skipped, and a new matter or vendor needs a new rule.

AI by content (Sortio AI Sort)
  How:    Reads the actual document, identifies client, matter,
          and doc type from the content, then files and renames.
  Good:   Handles garbage filenames, scans, and brand-new
          matters with no per-matter setup. One rule covers all.
  Bad:    Draws from your AI allowance and inference takes
          longer than regex.`}
          </pre>

          <p>
            For a firm, the realistic answer is a mix. Use AI Sort for the messy intake (downloads,
            email attachments, scans) where the filename is useless and the matter has to be read
            off the content. Once a stable, high-volume source emerges (your billing system always
            exports invoices the same way, for instance), promote that one flow to the AI Rule
            Builder so it runs deterministically without using your AI allowance. The comparison between
            the two Sortio modes is laid out in{' '}
            <Link to="/blog/ai-sort-vs-rule-builder-which-to-use" className="text-sortio-primary hover:underline">
              AI Sort vs Rule Builder
            </Link>
            . And to be clear about the macOS baseline: Finder itself has no auto rename or route
            rule engine, so the non-Sortio options are Hazel rules or manual filing.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How Sortio reads a document and identifies the matter
          </h2>

          <p>
            The flow is the same whether you run it on one document or a backlog of a thousand.
            With Analyze contents on, Sortio opens the file, extracts its text (an image-only scan
            gets on-device OCR on Mac and Windows, which reads its first pages), and reads it with
            an LLM. The model identifies the
            client and matter (from a matter number, a case caption, a re-line, or the parties
            named in the body), classifies the document type, and Sortio assembles the destination
            path and filename from your convention. Then it shows you a preview.
          </p>

          <p>
            Reading content is what makes this work where a rule cannot. A complaint, a discovery
            response, and an engagement letter can all arrive named document.pdf, and only the
            inside of the document tells them apart. Two matters for the same client are
            distinguished by the caption or matter number on the page, not by anything in the
            filename. An LLM is also robust to OCR noise on scanned signature pages, where a regex
            would simply stop matching. That robustness is the entire reason content-level routing
            holds up on a real firm&apos;s file mix.
          </p>

          <p>
            Nothing is destructive. Preview shows the proposed Client, Matter, Category, and new
            filename for every document before a single file moves. If Sortio routes a borderline
            document to the wrong matter, untick it in the preview so it stays where it is, or
            refine the prompt and run Preview again; nothing moves until you apply. Sortio keeps
            backup archives of your most recent sorts, so a misfile can be undone from inside the
            app.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A working Sortio prompt for matter-centric filing
          </h2>

          <p>
            Drop this into the Sortio prompt box, point it at the folder where loose documents
            accumulate (Downloads, a scan drop folder, or a shared "to file" folder), and run
            Preview before applying.
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Read each document and determine which client and which
matter it belongs to. Use the matter number if one appears
(format YYYY-NNNN), otherwise infer the client and matter
from the case caption, the re-line, or the parties named
in the body.

File the document under:
~/Clients/{Client}/{MatterNumber}_{MatterName}/{Category}/

Choose Category from exactly one of:
- Pleadings: complaints, answers, motions, briefs, orders,
  notices, anything filed with or issued by a court.
- Correspondence: letters and emails to or from the client,
  opposing counsel, or the court.
- Discovery: interrogatories, requests for production,
  responses, document productions, deposition materials.
- Contracts: agreements, engagement letters, signed
  instruments, term sheets.
- Billing: invoices, time exports, trust accounting,
  payment records.

Rename each file to:
{YYYY-MM-DD}_{MatterNumber}_{DocType}_{Description}.pdf
Use the document date (filing date, signing date, or letter
date). DocType is a short label (Motion, Letter, Discovery,
Engagement_Letter, Invoice). Keep the description short.

If you cannot confidently identify the matter, leave the
file in ~/Clients/_Unfiled/ and do not rename it.`}
          </pre>

          <p>
            Click Preview. Sortio shows the proposed client, matter, category, and new filename for
            every file. Anything it could not confidently match lands in an _Unfiled folder rather
            than being guessed into the wrong matter, which is the safe default for a legal filing
            system. Apply commits the moves once the paralegal or attorney has signed off.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Confidentiality: keep documents on the machine
          </h2>

          <p>
            Legal documents are privileged, and a lot of them are under protective orders or
            contain client confidences. Sortio supports local-only inference through Ollama: the
            LLM runs on the attorney&apos;s or paralegal&apos;s own machine (Llama 3, Mistral) and
            no document content ever leaves it. For privileged material, active discovery, or
            anything you would not send to a third-party AI provider, that is the configuration to
            use, and it is fully functional. The trade-off (local is a bit slower and slightly less
            accurate than managed AI) is laid out honestly in{' '}
            <Link to="/blog/local-ai-vs-cloud-ai-file-organization" className="text-sortio-primary hover:underline">
              local AI vs cloud AI for file organization
            </Link>
            . For routine, non-privileged filing the managed AI option is faster and more accurate.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            One shared rule for the whole firm
          </h2>

          <p>
            A filing convention only works if everyone follows it, and the way to guarantee that is
            to make it a shared rule rather than a wiki page. On Sortio Teams ($29 per seat per
            month) the matter-filing prompt and the folder conventions are shared across the firm
            with an admin console, so every paralegal and associate files identically. A new hire
            inherits the firm&apos;s convention on day one instead of learning one partner&apos;s
            personal habits, and there is one place to update the rule when the firm changes how it
            handles, say, trust accounting.
          </p>

          <p>
            This is the same workflow whether you have a one-attorney shop or a twenty-lawyer firm.
            For the firm-specific overview (multi-matter, multi-paralegal, with the privacy and
            shared-rule details) see the{' '}
            <Link to="/for-law-firms" className="text-sortio-primary hover:underline">
              Sortio for law firms
            </Link>{' '}
            page. The same content-reading approach extends past matters into per-client batching;
            for the general pattern see{' '}
            <Link to="/blog/batch-organize-documents-per-client" className="text-sortio-primary hover:underline">
              how to batch organize documents per client
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Where this sits relative to a document management system
          </h2>

          <p>
            Sortio is not a replacement for a legal document management system. It is the layer that
            runs before one. Email attachments, browser downloads, and scanned signature pages all
            land on a local disk with useless names long before anyone files them into the DMS.
            Sortio handles that messy local-disk stage: it reads each document, identifies the
            matter, names it to the firm convention, and routes it into the right Client {'>'}{' '}
            Matter {'>'} Category folder. What then goes into your DMS is already clean and
            consistently labeled, instead of a pile of document(4).pdf files someone has to rename
            on the way in.
          </p>

          <p>
            That clean, consistently named file is also what makes a matter defensible later. When
            a matter has to be produced, reviewed, or handed to successor counsel, a folder where
            every document carries its date, matter, and type is the difference between a smooth
            handoff and a scramble. For the broader version of that, see{' '}
            <Link to="/blog/make-client-files-audit-ready" className="text-sortio-primary hover:underline">
              how to make client files audit-ready
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/blog/enforce-file-naming-convention"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                How to enforce a file naming convention across a team
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/make-client-files-audit-ready"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                How to make client files audit-ready
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/batch-organize-documents-per-client"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                How to batch organize documents per client
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Try this on one messy matter</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance, no card required. Point Sortio at a folder
              of loose pleadings, letters, and scans, run Preview, and see them sorted into
              Pleadings, Correspondence, Discovery, Contracts, and Billing under the right matter
              before you apply anything. For the firm-wide setup, see Sortio for law firms.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/download"
                className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
              >
                Download Sortio
                <ArrowRight className="ml-2" size={16} />
              </Link>
              <Link
                to="/for-law-firms"
                className="inline-flex items-center px-6 py-3 border border-sortio-primary/40 text-apple-100 hover:bg-sortio-primary/10 font-medium rounded-xl transition-colors duration-200"
              >
                Sortio for law firms
                <ArrowRight className="ml-2" size={16} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OrganizeLawFirmFilesByMatterBlogPage;
